import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collectDescendantIds, formatInZone, roleAtLeast, type DrawingGroup, type DrawingSummary, type TrashedDrawingSummary } from '@system-atlas/shared';
import { ApiError, api } from '../lib/api';
import { Alert, Badge, Button, Card, ConfirmDialog, Label, Modal, Select, Spinner, TextInput } from '../components/ui';
import { GroupTree } from '../components/GroupTree';
import { ShareDialog, type ShareSubject } from '../components/ShareDialog';
import { useToast } from '../components/toast';
import { useAuth } from '../auth/AuthContext';
import { cn } from '../lib/utils';

type Sort = 'updated' | 'created' | 'name';
const SHARED = '__shared__';
const TRASH = '__trash__';

export default function DrawingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = !!user && roleAtLeast(user.role, 'admin');

  const [groups, setGroups] = useState<DrawingGroup[]>([]);
  const [drawings, setDrawings] = useState<DrawingSummary[] | null>(null);
  const [trash, setTrash] = useState<TrashedDrawingSummary[] | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<TrashedDrawingSummary | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('updated');
  const [error, setError] = useState<string | null>(null);
  // Gruppen-Sidebar ist auf Mobile standardmäßig eingeklappt (Zeichnungen zuerst), ab md immer sichtbar.
  const [groupsOpen, setGroupsOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<DrawingSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DrawingSummary | null>(null);
  const [shareSubject, setShareSubject] = useState<ShareSubject | null>(null);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<DrawingGroup | null>(null);
  const [createChildParent, setCreateChildParent] = useState<DrawingGroup | null>(null);
  const [renameGroupTarget, setRenameGroupTarget] = useState<DrawingGroup | null>(null);
  const [moveGroupTarget, setMoveGroupTarget] = useState<DrawingGroup | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const importInputRef = useRef<HTMLInputElement>(null);

  const onImportFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    void run(async () => {
      const { drawing } = await api.importDrawing(file);
      await loadDrawings();
      navigate(`/drawings/${drawing.id}`);
    });
  };

  const groupName = useMemo(() => {
    const map = new Map(groups.map((g) => [g.id, g.name]));
    return (id: string | null) => (id ? (map.get(id) ?? '') : '');
  }, [groups]);

  const loadGroups = async () => {
    const { groups: rows } = await api.listGroups();
    setGroups(rows);
  };

  const loadDrawings = async () => {
    if (selectedGroup === TRASH) {
      const { drawings: rows } = await api.listTrash();
      setTrash(rows);
      return;
    }
    const isShared = selectedGroup === SHARED;
    const { drawings: rows } = await api.listDrawings({
      search: search || undefined,
      scope: isShared ? 'shared' : 'all',
      groupId: isShared ? undefined : (selectedGroup ?? undefined),
      includeSubgroups: !isShared && !!selectedGroup,
      sort,
    });
    setDrawings(rows);
  };

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    }
  };

  useEffect(() => {
    void run(loadGroups);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => void run(loadDrawings), 200);
    return () => clearTimeout(timer);
  }, [search, selectedGroup, sort]);

  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 py-8 md:grid-cols-[220px_1fr]">
      {/* Mobiler Umschalter für die Gruppen-Sidebar (ab md ausgeblendet, da die Sidebar dort fest steht). */}
      <button
        type="button"
        onClick={() => setGroupsOpen((v) => !v)}
        aria-expanded={groupsOpen}
        aria-controls="group-sidebar"
        className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm font-medium hover:bg-slate-200/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 md:hidden dark:border-slate-700 dark:hover:bg-slate-700/40"
      >
        <span>{t('groups.title')}</span>
        <span aria-hidden="true">{groupsOpen ? '▲' : '▼'}</span>
      </button>

      {/* Sidebar: Gruppen */}
      <aside id="group-sidebar" className={cn('space-y-2 md:block', groupsOpen ? 'block' : 'hidden')}>
        <h2 className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('groups.title')}</h2>
        <button
          type="button"
          onClick={() => setSelectedGroup(null)}
          className={
            'w-full rounded-md px-3 py-1.5 text-left text-sm ' +
            (selectedGroup === null ? 'bg-slate-200 font-medium dark:bg-slate-700' : 'hover:bg-slate-200/60 dark:hover:bg-slate-700/60')
          }
        >
          {t('drawings.allGroups')}
        </button>
        <button
          type="button"
          onClick={() => setSelectedGroup(SHARED)}
          className={
            'w-full rounded-md px-3 py-1.5 text-left text-sm ' +
            (selectedGroup === SHARED ? 'bg-slate-200 font-medium dark:bg-slate-700' : 'hover:bg-slate-200/60 dark:hover:bg-slate-700/60')
          }
        >
          {t('drawings.sharedWithMe')}
        </button>
        <button
          type="button"
          onClick={() => setSelectedGroup(TRASH)}
          className={
            'w-full rounded-md px-3 py-1.5 text-left text-sm ' +
            (selectedGroup === TRASH ? 'bg-slate-200 font-medium dark:bg-slate-700' : 'hover:bg-slate-200/60 dark:hover:bg-slate-700/60')
          }
        >
          {t('drawings.trash')}
        </button>
        <GroupTree
          groups={groups}
          selectedId={selectedGroup === SHARED ? null : selectedGroup}
          onSelect={setSelectedGroup}
          onCreateChild={setCreateChildParent}
          onRename={setRenameGroupTarget}
          onMove={setMoveGroupTarget}
          onDelete={setDeleteGroupTarget}
          onShare={(g) => setShareSubject({ type: 'drawing_group', id: g.id, name: g.name })}
        />
        <form
          className="flex gap-1 pt-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newGroupName.trim()) return;
            void run(async () => {
              await api.createGroup({ name: newGroupName.trim() });
              setNewGroupName('');
              await loadGroups();
            });
          }}
        >
          <TextInput
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder={t('groups.newPlaceholder')}
            className="text-sm"
          />
          <Button type="submit" variant="secondary">+</Button>
        </form>
      </aside>

      {/* Hauptbereich */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="mr-auto text-2xl font-bold tracking-tight">{t('drawings.title')}</h1>
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('drawings.searchPlaceholder')}
            className="max-w-xs"
          />
          <Select value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="updated">{t('drawings.sortUpdated')}</option>
            <option value="created">{t('drawings.sortCreated')}</option>
            <option value="name">{t('drawings.sortName')}</option>
          </Select>
          <Button variant="secondary" onClick={() => importInputRef.current?.click()}>
            {t('drawings.import')}
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept=".drawio,.xml,.vsdx"
            className="hidden"
            onChange={onImportFile}
          />
          <Button onClick={() => setCreateOpen(true)}>{t('drawings.new')}</Button>
        </div>

        {error && <Alert kind="error">{error}</Alert>}

        {selectedGroup === TRASH ? (
          !trash ? (
            <Spinner />
          ) : trash.length === 0 ? (
            <Card>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('drawings.trashEmpty')}</p>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {trash.map((d) => (
                <Card key={d.id} className="flex flex-col gap-3">
                  <h3 className="font-semibold">{d.name}</h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    {!d.owned && (
                      <Badge tone="neutral">{t('drawings.trashOwnerLabel', { name: d.ownerName ?? d.ownerEmail ?? '?' })}</Badge>
                    )}
                    <span>{t('drawings.trashDeletedAt', { date: formatInZone(d.deletedAt) })}</span>
                  </div>
                  <div className="mt-auto flex flex-wrap items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={() =>
                        void run(async () => {
                          await api.restoreDrawing(d.id);
                          await loadDrawings();
                          toast.success(t('drawings.restoredToast'));
                        })
                      }
                    >
                      {t('drawings.restore')}
                    </Button>
                    {isAdmin && (
                      <Button variant="danger" onClick={() => setPurgeTarget(d)}>
                        {t('drawings.purge')}
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )
        ) : !drawings ? (
          <Spinner />
        ) : drawings.length === 0 ? (
          <Card>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('drawings.empty')}</p>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {drawings.map((d) => (
              <Card key={d.id} className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/drawings/${d.id}`)}
                  className="text-left"
                >
                  <h3 className="font-semibold hover:underline">{d.name}</h3>
                  {d.description && (
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">{d.description}</p>
                  )}
                </button>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                  {!d.owned && <Badge tone="amber">{t('drawings.shared')}</Badge>}
                  {d.groupId && d.owned && <Badge tone="neutral">{groupName(d.groupId)}</Badge>}
                  <span>{t('drawings.updatedAt', { date: formatInZone(d.updatedAt) })}</span>
                </div>
                {d.owned ? (
                  <div className="mt-auto flex flex-wrap items-center gap-2">
                    <Select
                      value={d.groupId ?? ''}
                      aria-label={t('drawings.changeGroupFor', { name: d.name })}
                      onChange={(e) =>
                        void run(async () => {
                          await api.updateDrawing(d.id, { groupId: e.target.value || null });
                          await loadDrawings();
                        })
                      }
                      className="flex-1 text-xs"
                    >
                      <option value="">{t('drawings.noGroup')}</option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </Select>
                    <Button variant="ghost" onClick={() => setShareSubject({ type: 'drawing', id: d.id, name: d.name })}>
                      {t('drawings.share')}
                    </Button>
                    <Button variant="ghost" onClick={() => setRenameTarget(d)}>
                      {t('drawings.rename')}
                    </Button>
                    <Button variant="ghost" onClick={() => setDeleteTarget(d)}>
                      {t('drawings.delete')}
                    </Button>
                  </div>
                ) : (
                  <div className="mt-auto text-xs text-slate-400">
                    {t(`share.permission.${d.access === 'edit' ? 'edit' : 'view'}`)}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </section>

      <CreateDrawingModal
        open={createOpen}
        groups={groups}
        onClose={() => setCreateOpen(false)}
        onCreated={async (id) => {
          setCreateOpen(false);
          await loadDrawings();
          navigate(`/drawings/${id}`);
        }}
        onError={setError}
      />

      <RenameDrawingModal
        target={renameTarget}
        onClose={() => setRenameTarget(null)}
        onSaved={async () => {
          setRenameTarget(null);
          await loadDrawings();
          toast.success(t('drawings.renamedToast'));
        }}
        onError={setError}
      />

      <Modal open={!!deleteTarget} title={t('drawings.deleteTitle')} onClose={() => setDeleteTarget(null)}>
        <p className="text-sm">{t('drawings.deleteConfirm', { name: deleteTarget?.name ?? '' })}</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              void run(async () => {
                if (deleteTarget) await api.deleteDrawing(deleteTarget.id);
                setDeleteTarget(null);
                await loadDrawings();
                toast.success(t('drawings.deletedToast'));
              })
            }
          >
            {t('drawings.delete')}
          </Button>
        </div>
      </Modal>

      <ShareDialog subject={shareSubject} onClose={() => setShareSubject(null)} />

      <Modal open={!!purgeTarget} title={t('drawings.purgeTitle')} onClose={() => setPurgeTarget(null)}>
        <p className="text-sm">{t('drawings.purgeConfirm', { name: purgeTarget?.name ?? '' })}</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setPurgeTarget(null)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              void run(async () => {
                if (purgeTarget) await api.purgeDrawing(purgeTarget.id);
                setPurgeTarget(null);
                await loadDrawings();
                toast.success(t('drawings.purgedToast'));
              })
            }
          >
            {t('drawings.purge')}
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteGroupTarget}
        title={t('groups.delete')}
        message={t('groups.deleteConfirm', { name: deleteGroupTarget?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onCancel={() => setDeleteGroupTarget(null)}
        onConfirm={() =>
          void run(async () => {
            const group = deleteGroupTarget;
            setDeleteGroupTarget(null);
            if (!group) return;
            await api.deleteGroup(group.id);
            if (selectedGroup === group.id) setSelectedGroup(null);
            await loadGroups();
            await loadDrawings();
            toast.success(t('groups.deletedToast'));
          })
        }
      />

      <GroupNameModal
        open={!!createChildParent}
        title={t('groups.createSubgroupTitle', { name: createChildParent?.name ?? '' })}
        initialName=""
        onClose={() => setCreateChildParent(null)}
        onSubmit={(name) =>
          void run(async () => {
            const parent = createChildParent;
            setCreateChildParent(null);
            if (!parent) return;
            await api.createGroup({ name, parentId: parent.id });
            await loadGroups();
          })
        }
      />

      <GroupNameModal
        open={!!renameGroupTarget}
        title={t('groups.renameTitle')}
        initialName={renameGroupTarget?.name ?? ''}
        onClose={() => setRenameGroupTarget(null)}
        onSubmit={(name) =>
          void run(async () => {
            const group = renameGroupTarget;
            setRenameGroupTarget(null);
            if (!group) return;
            await api.updateGroup(group.id, { name });
            await loadGroups();
          })
        }
      />

      <MoveGroupModal
        target={moveGroupTarget}
        groups={groups}
        onClose={() => setMoveGroupTarget(null)}
        onSubmit={(parentId) =>
          void run(async () => {
            const group = moveGroupTarget;
            setMoveGroupTarget(null);
            if (!group) return;
            await api.updateGroup(group.id, { parentId });
            await loadGroups();
          })
        }
      />
    </div>
  );
}

function GroupNameModal({
  open,
  title,
  initialName,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  initialName: string;
  onClose: () => void;
  onSubmit: (name: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          onSubmit(name.trim());
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <Label htmlFor="gn-name">{t('drawings.name')}</Label>
          <TextInput id="gn-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">{t('drawings.save')}</Button>
        </div>
      </form>
    </Modal>
  );
}

function MoveGroupModal({
  target,
  groups,
  onClose,
  onSubmit,
}: {
  target: DrawingGroup | null;
  groups: DrawingGroup[];
  onClose: () => void;
  onSubmit: (parentId: string | null) => void;
}) {
  const { t } = useTranslation();
  const [parentId, setParentId] = useState('');

  useEffect(() => {
    if (target) setParentId(target.parentId ?? '');
  }, [target]);

  // Eigener Teilbaum (selbst + Nachfahren) ist kein gültiges Ziel.
  const blocked = useMemo(() => {
    if (!target) return new Set<string>();
    return new Set([target.id, ...collectDescendantIds(groups, target.id)]);
  }, [groups, target]);

  return (
    <Modal open={!!target} title={t('groups.moveTitle', { name: target?.name ?? '' })} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(parentId || null);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <Label htmlFor="mg-parent">{t('groups.moveTargetLabel')}</Label>
          <Select id="mg-parent" className="w-full" value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">{t('groups.noParent')}</option>
            {groups
              .filter((g) => !blocked.has(g.id))
              .map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit">{t('drawings.save')}</Button>
        </div>
      </form>
    </Modal>
  );
}

function CreateDrawingModal({
  open,
  groups,
  onClose,
  onCreated,
  onError,
}: {
  open: boolean;
  groups: DrawingGroup[];
  onClose: () => void;
  onCreated: (id: string) => void | Promise<void>;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [groupId, setGroupId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { drawing } = await api.createDrawing({
        name,
        description: description || undefined,
        groupId: groupId || null,
      });
      setName('');
      setDescription('');
      setGroupId('');
      await onCreated(drawing.id);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title={t('drawings.createTitle')} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="d-name">{t('drawings.name')}</Label>
          <TextInput id="d-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="d-desc">{t('drawings.description')}</Label>
          <TextInput id="d-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="d-group">{t('drawings.group')}</Label>
          <Select id="d-group" className="w-full" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">{t('drawings.noGroup')}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? t('common.pleaseWait') : t('drawings.create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function RenameDrawingModal({
  target,
  onClose,
  onSaved,
  onError,
}: {
  target: DrawingSummary | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onError: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) setName(target.name);
  }, [target]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target) return;
    setBusy(true);
    try {
      await api.updateDrawing(target.id, { name });
      await onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={!!target} title={t('drawings.renameTitle')} onClose={onClose}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div>
          <Label htmlFor="r-name">{t('drawings.name')}</Label>
          <TextInput id="r-name" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? t('common.pleaseWait') : t('drawings.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
